import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';

import { Typography } from '@mui/material';
import moment from 'moment';

import { formatDurationFromTimestamp } from '@/utils';

interface TimerDisplayProps {
  customStatusUpdatedAt: string | null;
  isAssignedToCFS?: boolean; // Accept CFS assignment status
  onThresholdExceededChange?: (exceeded: boolean) => void; // Callback for threshold status
}

const TimerDisplay: React.FC<TimerDisplayProps> = ({
  customStatusUpdatedAt,
  isAssignedToCFS = false,
  onThresholdExceededChange,
}) => {
  const [formattedDuration, setFormattedDuration] = useState<string>('N/A');
  const [isThresholdExceeded, setIsThresholdExceeded] =
    useState<boolean>(false);
  const [timerColor, setTimerColor] = useState<string>('inherit');

  // Memoize the callback to prevent unnecessary effect runs
  const handleThresholdChange = useCallback(
    (exceeded: boolean) => {
      if (onThresholdExceededChange) {
        onThresholdExceededChange(exceeded);
      }
    },
    [onThresholdExceededChange],
  );

  useEffect(() => {
    if (!customStatusUpdatedAt) {
      setFormattedDuration('N/A');
      setTimerColor('inherit');
      if (isThresholdExceeded) {
        setIsThresholdExceeded(false);
        handleThresholdChange(false);
      }
      return () => {};
    }

    let intervalId: NodeJS.Timeout | null = null;

    const calculateAndUpdate = () => {
      // Update formatted duration
      setFormattedDuration(formatDurationFromTimestamp(customStatusUpdatedAt));

      // Check threshold
      const startTime = moment(customStatusUpdatedAt);
      const now = moment();
      // eslint-disable-next-line import/no-named-as-default-member
      const dur = moment.duration(now.diff(startTime));
      const totalMinutes = dur.asMinutes();

      const thresholdMinutes = isAssignedToCFS ? 5 : 60;
      const currentlyExceeded =
        totalMinutes >= 0 && totalMinutes >= thresholdMinutes;

      // Set text color based on threshold
      setTimerColor(currentlyExceeded ? 'error.main' : 'inherit');

      if (currentlyExceeded !== isThresholdExceeded) {
        setIsThresholdExceeded(currentlyExceeded);
        handleThresholdChange(currentlyExceeded);
      }
    };

    calculateAndUpdate();
    intervalId = setInterval(calculateAndUpdate, 1000); // Update every second for threshold check

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    customStatusUpdatedAt,
    isAssignedToCFS,
    isThresholdExceeded,
    handleThresholdChange,
  ]);

  // Apply the color state to the typography
  return (
    <Typography sx={{ color: timerColor }}>{formattedDuration}</Typography>
  );
};

export default TimerDisplay;
